/**
 * Reminders on this phone (v1 ticket 38): reading what the browser can do, turning them on (the permission prompt
 * only in the tap, a test push as proof, the browser's daily check as the fallback) and off. The rules that need no
 * browser are in `reminder-state.ts`. Nothing here logs or sends an endpoint, a key or a payload anywhere but to the
 * app's own server, and nothing reaches diag.
 */
import { api, HttpError } from './api';
import { currentLang } from './i18n';
import { isIos, keyBytes, rememberedDevice, withDevice, type DeviceFacts } from './reminder-state';
import { confirmed, currentOwner } from './session';

const DEVICE_KEY = 'li-reminder-device';
const LANG_CACHE = 'eaves-reminders-v1';
const LANG_KEY = '/__eaves-reminder-lang';
const DEVICE_CACHE_KEY = '/__eaves-reminder-device';
const SYNC_KEY = 'li-reminder-sync';
const SYNC_TAG = 'eaves-reminder';
/** How long a test push has to arrive, and how often the server is asked meanwhile. */
export const PROOF_MS = 30_000;
/** A test push that has not shown by now is sent once more (a fresh grant can lose the first). */
export const RESEND_MS = 12_000;
const POLL_MS = 2000;
/** A browser journey shortens the wait with `window.__eavesProofMs`; nothing else sets it. */
const proofMs = () => (globalThis as { __eavesProofMs?: number }).__eavesProofMs ?? PROOF_MS;

const store = (): Storage | undefined => {
  try { return globalThis.localStorage; } catch { return undefined; }
};

export const deviceId = (): string | null => rememberedDevice(store()?.getItem(DEVICE_KEY) ?? null, currentOwner());

function setDeviceId(id: string | null) {
  const owner = currentOwner();
  if (!owner) return;
  try { store()?.setItem(DEVICE_KEY, withDevice(store()?.getItem(DEVICE_KEY) ?? null, owner, id)); } catch { /* full or private: the sheet reads the server */ }
  if (!id) setSyncDevice(false);
}

/** Whether this phone's device is the daily check's (only that one claims a day's slot, by its id). */
function setSyncDevice(on: boolean) {
  try {
    if (on) store()?.setItem(SYNC_KEY, '1');
    else store()?.removeItem(SYNC_KEY);
  } catch { /* the claim then names no device */ }
}

async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined;
  try { return await navigator.serviceWorker.getRegistration(); } catch { return undefined; }
}

/** What this phone can do, read now (a registered service worker is needed, so dev mode reads as unsupported). */
export async function readFacts(devices: DeviceFacts['devices']): Promise<DeviceFacts> {
  const reg = await registration();
  const supported = !!reg?.active && 'Notification' in window && 'PushManager' in window;
  const standalone = matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return {
    supported,
    ios: isIos(navigator.userAgent, navigator.maxTouchPoints),
    standalone,
    permission: 'Notification' in window ? Notification.permission : 'default',
    remembered: deviceId(),
    devices,
  };
}

/**
 * The interface language, kept in a small cache for a reminder the worker shows without the server, and this
 * learner's sync device id (when the daily check is how this phone is reached) for the worker's claim.
 */
export async function rememberLang(): Promise<void> {
  try {
    const cache = await caches.open(LANG_CACHE);
    await cache.put(LANG_KEY, new Response(currentLang()));
    const device = store()?.getItem(SYNC_KEY) ? deviceId() : null;
    if (device) await cache.put(DEVICE_CACHE_KEY, new Response(device));
    else await cache.delete(DEVICE_CACHE_KEY);
  } catch { /* the worker falls back to English and an unnamed claim */ }
}

/** Forgets the device id the worker would claim with (turn off, sign-out). */
async function forgetCachedDevice(): Promise<void> {
  try { await (await caches.open(LANG_CACHE)).delete(DEVICE_CACHE_KEY); } catch { /* nothing kept */ }
}

/** On each app start while this phone has reminders on. */
export function refreshLang(): void {
  if (confirmed() && deviceId()) void rememberLang();
}

/** `dismissed`: the permission prompt was closed without an answer, so nothing is wrong and nothing is said. */
export type TurnOn = 'on-push' | 'on-sync' | 'denied' | 'dismissed' | 'cant';

const tz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { return undefined; }
};

/** Resolves true when the test push is shown (the worker's message, or the server saying so) within `PROOF_MS`. */
function proof(id: string, resend: () => Promise<unknown>): Promise<boolean> {
  return new Promise((done) => {
    let over = false;
    const finish = (ok: boolean) => {
      if (over) return;
      over = true;
      navigator.serviceWorker.removeEventListener('message', onMessage);
      clearInterval(poll);
      clearTimeout(timer);
      clearTimeout(again);
      done(ok);
    };
    const onMessage = (e: MessageEvent) => { if ((e.data as { type?: string } | null)?.type === 'reminder-shown') finish(true); };
    navigator.serviceWorker.addEventListener('message', onMessage);
    const poll = setInterval(() => {
      void api.reminders().then((s) => { if (s.devices.some((d) => d.id === id && d.confirmed)) finish(true); }).catch(() => undefined);
    }, POLL_MS);
    const timer = setTimeout(() => finish(false), proofMs());
    // One more test push, sent the same way; whatever it answers is ignored and the window runs on.
    const again = setTimeout(() => { void resend().catch(() => undefined); }, Math.min(RESEND_MS, proofMs() * 0.4));
  });
}

async function periodicAllowed(reg: ServiceWorkerRegistration): Promise<boolean> {
  const ps = (reg as ServiceWorkerRegistration & { periodicSync?: { register(tag: string, o: { minInterval: number }): Promise<void> } }).periodicSync;
  if (!ps) return false;
  try {
    const q = await navigator.permissions.query({ name: 'periodic-background-sync' as PermissionName });
    return q.state === 'granted';
  } catch {
    return false;
  }
}

async function dailyCheck(reg: ServiceWorkerRegistration): Promise<boolean> {
  if (!(await periodicAllowed(reg))) return false;
  const ps = (reg as unknown as { periodicSync: { register(tag: string, o: { minInterval: number }): Promise<void>; unregister(tag: string): Promise<void> } }).periodicSync;
  try {
    await ps.register(SYNC_TAG, { minInterval: 12 * 60 * 60 * 1000 });
  } catch {
    return false;
  }
  try {
    const { id } = await api.addReminderDevice({ channel: 'sync', tz: tz(), fallback: true });
    setDeviceId(id);
    setSyncDevice(true);
    void rememberLang();
    return true;
  } catch {
    await ps.unregister(SYNC_TAG).catch(() => undefined);
    return false;
  }
}

/**
 * Turn on, in the tap: the permission prompt, a push subscription, the server's test push as proof; failing that the
 * browser's daily check; failing that nothing is left behind. `checking` is called once the proof is awaited.
 */
export async function turnOn(publicKey: string, checking: () => void): Promise<TurnOn> {
  // The prompt is the very first thing in the tap, before any await, so iOS keeps the tap's user activation.
  const asked = 'Notification' in window && 'PushManager' in window && 'serviceWorker' in navigator ? Notification.requestPermission() : null;
  const reg = await registration();
  if (!asked || !reg?.active) return 'cant';
  const perm = await asked;
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'dismissed';
  void rememberLang();
  let sub: PushSubscription | null = null;
  let id = '';
  try {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
    const register = () => api.addReminderDevice({ channel: 'push', subscription: sub!.toJSON(), tz: tz() });
    ({ id } = await register());
    setDeviceId(id);
    checking();
    if (await proof(id, register)) return 'on-push';
  } catch (e) {
    // The server refusing the test (too many today) is shown as it is; a push service we can't use falls back.
    if (e instanceof HttpError && e.code !== 'reminder-endpoint') {
      if (id) await api.removeReminderDevice(id).catch(() => undefined);
      await sub?.unsubscribe().catch(() => undefined);
      setDeviceId(null);
      throw e;
    }
  }
  if (id) { await api.removeReminderDevice(id).catch(() => undefined); setDeviceId(null); }
  await sub?.unsubscribe().catch(() => undefined);
  if (await dailyCheck(reg)) return 'on-sync';
  return 'cant';
}

/** Sign-out: turn this phone off while the session still exists, bounded, and never in the way of signing out. */
export async function turnOffForSignOut(ms = 3000): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([turnOff(), new Promise<void>((_, no) => { timer = setTimeout(() => no(new Error('slow')), ms); })]);
  } catch {
    forgetDevice();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Turn off on this phone: the local half (unsubscribe, unregister the daily check, forget the device) always runs,
 * then the server's device is deleted. A server that can't be reached throws after the phone is already off; the push
 * service then answers 404/410 and the server drops the device itself.
 */
export async function turnOff(): Promise<void> {
  const reg = await registration();
  const id = deviceId();
  setDeviceId(null);
  await forgetCachedDevice();
  try { await (await reg?.pushManager.getSubscription())?.unsubscribe(); } catch { /* already gone */ }
  try {
    await (reg as unknown as { periodicSync?: { unregister(tag: string): Promise<void> } } | undefined)?.periodicSync?.unregister(SYNC_TAG);
  } catch { /* none registered */ }
  if (id) await api.removeReminderDevice(id).catch((e) => { if (!(e instanceof HttpError && e.status === 404)) throw e; });
}

/** A stopped device (the server forgot it): clear what this phone kept before turning on again. */
export const forgetDevice = () => { setDeviceId(null); void forgetCachedDevice(); };
